import Cookies from 'js-cookie';
import { ILoggerDetails, IEmServiceDetails } from '../types/types';
import { LogDebug } from './Logging';
import { getFirstOrDefaultLoggedInHost, getLoggedInUrlFromPublicUrl, getRouteByTier } from './RedirectRouteMapper';
import { getServiceTierByUser, hasLoggedInAlternativeService } from './TierHelper';
import { ACCESS_TOKEN_COOKIE_NAME, SERVICE_TIER_UNKNOWN, ServiceTier } from '../constants/Constants';

/**
 * This is a CLIENT-SIDE function that generates a login URL for the user.
 *
 * logic used to live in https://github.com/eMarketer/Common.ComponentLibrary/blob/main/src/helpers/utils/getEquivalentProUrl.ts#L7
 * and: https://github.com/eMarketer/Common.ComponentLibrary/blob/main/src/helpers/Subscriptions.ts#L15
 * and: https://github.com/eMarketer/Common.ComponentLibrary/blob/main/src/helpers/utils/getEquivalentProUrl.ts#L7
 *
 * But it was moved into this library to make it more reusable and to make it easier to test. See readme/wikis for more info.
 *
 * @param isProServiceAvailable
 * @param emReAuthenticationServiceBaseUrl
 * @param logger logger instance for logging messages, (or `console` for client-side logging)
 * @param logLevel log level to use for logging messages (if you select 'debug', it will log all messages, otherwise only 'info' and above)
 * @returns
 */
export function getLoginButtonLink(
  emReAuthenticationServiceBaseUrl: string,
  emService: IEmServiceDetails,
  logger?: ILoggerDetails
): string {
  // If public service doesn't have a matching PRO service, then can't redirect there, so redirect to login page
  if (!hasLoggedInAlternativeService(emService)) {
    return emReAuthenticationServiceBaseUrl;
  }

  // being fancy - this will allow us to get the correct pod for role right away and minimize redirects
  let userTier: ServiceTier = SERVICE_TIER_UNKNOWN;

  // try get cookies
  if (typeof document !== 'undefined') {
    try {
      const em_at = Cookies.get(ACCESS_TOKEN_COOKIE_NAME);
      userTier = getServiceTierByUser(em_at);
      LogDebug(logger, `getLoginButtonLink() called with em_at: ${em_at}`);
    } catch (error) {
      LogDebug(logger, `getLoginButtonLink() cookie extraction failed: ${error}`);
      // Continue with undefined em_at cookie
    }
  }

  // Get the host to redirect to
  let newHost = null;
  if (userTier != SERVICE_TIER_UNKNOWN) {
    newHost = getRouteByTier(emService, userTier);
  } else {
    newHost = getFirstOrDefaultLoggedInHost(emService);
  }

  // assemble a complete "logged-in URL"
  const proUrl = newHost ? getLoggedInUrlFromPublicUrl(window.location.href, newHost, logger) : null;
  LogDebug(logger, `getLoginButtonLink() :: calculated proUrl is ${proUrl}`);

  //
  if (!proUrl || proUrl === window.location.href) {
    return emReAuthenticationServiceBaseUrl;
  }

  return `${emReAuthenticationServiceBaseUrl}/Account/Login?returnUrl=${encodeURIComponent(proUrl)}`;
}
